import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Section";

const RELAY_WORDS = [
  { t: "Sense", c: "#00e5ff", g: "0,229,255" },
  { t: "Understand", c: "#3ad6a0", g: "58,214,160" },
  { t: "Prompt", c: "#e6c068", g: "230,192,104" },
  { t: "Act", c: "#ff2d78", g: "255,45,120" },
  { t: "Learn.", c: "#b794f6", g: "183,148,246" },
];

const BANNER_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/89fc1ypv_sensor%20domino%20line.jpg";

// Scroll-triggered relay: each word ignites in sequence (~1s apart), glows, then
// settles; a pair of arrows continuously pulses between words, carrying energy
// toward the next one. Replays on re-entry.
function SenseRelay({ idPrefix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.6 });
  return (
    <h2
      ref={ref}
      className="font-heading font-light tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl flex flex-wrap items-center justify-center gap-x-2 gap-y-1 md:gap-x-4"
      data-testid={`${idPrefix}-sense-banner-headline`}
    >
      {RELAY_WORDS.map((w, i) => (
        <span key={w.t} className="flex items-center gap-x-2 md:gap-x-4">
          <motion.span
            data-testid={`${idPrefix}-relay-word-${i}`}
            style={{ color: w.c }}
            initial={{ opacity: 0, y: 10 }}
            animate={
              inView
                ? {
                    opacity: [0, 1, 1, 1],
                    y: [10, 0, 0, 0],
                    textShadow: [
                      `0 0 0px rgba(${w.g},0)`,
                      `0 0 28px rgba(${w.g},0.95)`,
                      `0 0 28px rgba(${w.g},0.95)`,
                      `0 0 8px rgba(${w.g},0.35)`,
                    ],
                  }
                : { opacity: 0, y: 10, textShadow: `0 0 0px rgba(${w.g},0)` }
            }
            transition={{ delay: i * 1.0, duration: 1.2, times: [0, 0.22, 0.72, 1], ease: "easeOut" }}
          >
            {w.t}
          </motion.span>
          {i < RELAY_WORDS.length - 1 && (
            <span className="flex items-center gap-0.5 md:gap-1" aria-hidden="true">
              {[0, 1].map((a) => (
                <motion.span
                  key={a}
                  className="flex"
                  initial={{ opacity: 0 }}
                  animate={
                    inView
                      ? {
                          opacity: [0.12, 1, 0.35, 0.12],
                          x: [-3, 2, 4, -3],
                          filter: [
                            "drop-shadow(0 0 0px rgba(255,45,120,0))",
                            "drop-shadow(0 0 8px rgba(255,45,120,0.85))",
                            "drop-shadow(0 0 3px rgba(255,45,120,0.3))",
                            "drop-shadow(0 0 0px rgba(255,45,120,0))",
                          ],
                        }
                      : { opacity: 0 }
                  }
                  transition={{
                    delay: i * 1.0 + 0.5 + a * 0.16,
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 0.25,
                    ease: "easeInOut",
                  }}
                >
                  <ChevronRight className="text-accent" size={24} strokeWidth={3} />
                </motion.span>
              ))}
            </span>
          )}
        </span>
      ))}
    </h2>
  );
}

export function SenseRelayBanner({
  idPrefix = "sense",
  eyebrow = "The Next Layer of the Platform",
  subtitle = "Most wearables stop at sensing. WearSpray is built to close the loop, turning information into something you can act on, right from the wrist.",
}) {
  return (
    <section className="relative w-full overflow-hidden border-t border-zinc-800 bg-black" data-testid={`${idPrefix}-sense-banner`}>
      <div className="relative h-[68vh] min-h-[460px] md:h-[80vh] w-full">
        <img loading="lazy"
          src={BANNER_IMG}
          alt="A receding line of WearSpray wearable dispensers in a spectrum of colors on a dark reflective surface"
          className="absolute inset-0 w-full h-full object-cover object-center"
          data-testid={`${idPrefix}-sense-banner-image`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/20 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 md:px-12">
          <Reveal>
            <span className="font-mono text-[11px] md:text-xs tracking-[0.3em] uppercase text-accent block mb-6 md:mb-8" data-testid={`${idPrefix}-sense-banner-eyebrow`}>
              {eyebrow}
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <SenseRelay idPrefix={idPrefix} />
          </Reveal>
          <Reveal delay={0.16}>
            <p className="font-body text-zinc-300 text-base md:text-lg leading-relaxed mt-7 md:mt-9 max-w-2xl mx-auto">
              {subtitle}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
